import { Controller } from '@hotwired/stimulus'
import { ComparisonState } from '../comparison-state'
import { UIManager } from '../ui-manager'
import { CheckboxHandler } from '../checkbox-handler'

/**
 * Stimulus controller for product comparison widget
 * Orchestrates interactions between state, UI, and user input
 */
export default class extends Controller {
  static targets = [
    'container',
    'widget',
    'counter',
    'compareButton',
    'compareButtonLabel',
    'clearButton',
    'compareContainer',
    'twoItemText',
    'buttonGroup',
    'hiddenField'
  ]

  static values = {
    minimumCount: { type: Number, default: 1 },
    disableLimit: { type: Number, default: 2 },
    maxCount: { type: Number, default: 5 },
    compareUrl: { type: String }
  }

  connect() {
    this.initializeManagers()
    this.loadStoredComparisons()
    this.updateUI()
    this.attachEventHandlers()
  }

  initializeManagers() {
    this.state = new ComparisonState()
    this.uiManager = new UIManager(this, this)
    this.checkboxHandler = new CheckboxHandler(
      this.state,
      this.uiManager,
      this.maxCountValue
    )
  }

  loadStoredComparisons() {
    const storedData = this.state.loadFromStorage()
    
    if (!storedData.hasData) return
    
    this.uiManager.removeAllCompareProducts()
    
    for (let i = 0; i < storedData.ids.length; i++) {
      const $element = this.uiManager.createCompareElement(
        storedData.ids[i],
        storedData.images[i],
        storedData.descriptions[i]
      )
      this.uiManager.addCompareElement($element, this.compareContainerTarget)
      this.uiManager.checkCheckbox(storedData.ids[i])
    }
    
    // Update hidden field with stored IDs
    this.uiManager.updateHiddenField(storedData.ids)
  }

  updateUI() {
    const count = this.uiManager.getProductCount()
    this.uiManager.updateButtonStates(count)
    this.uiManager.updateCounter(count)
  }

  attachEventHandlers() {
    this.checkboxHandler.attach()
  }

  async loadCompareData(event) {
    if (event) event.preventDefault()
    
    const comparedValues = this.hasHiddenFieldTarget ? this.hiddenFieldTarget.value : ''
    
    if (!comparedValues) {
      // Silently return if no products selected
      return
    }
    
    try {
      const response = await fetch(`/categories/compare?compared_values=${comparedValues}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) throw new Error('Failed to load comparison data')
      
      const data = await response.json()
      const holder = document.getElementById('compare-holder')
      
      if (holder) {
        holder.innerHTML = data.html
        
        // Trigger modal open using Bootstrap 5 API
        const modalElement = document.getElementById('compareModal')
        if (modalElement && window.bootstrap) {
          // Add listener to move focus before modal hides to prevent aria-hidden focus issue
          modalElement.addEventListener('hide.bs.modal', () => {
            if (modalElement.contains(document.activeElement)) {
              this.compareButtonTarget?.focus()
            }
          }, { once: true })
          
          const modal = new window.bootstrap.Modal(modalElement)
          modal.show()
        } else {
          console.error('Modal element or Bootstrap not found')
        }
      }
    } catch (error) {
      console.error('Error loading comparison data:', error)
    }
  }

  toggleWidget() {
    this.uiManager.toggleWidget()
  }

  removeProduct(event) {
    const button = event.currentTarget
    const id = button.dataset.productId
    const image = button.dataset.productImage
    const description = button.dataset.productDescription
    
    this.uiManager.uncheckCheckbox(id)
    $(button).parent().remove()
    
    this.state.remove(id, image, description)
    this.uiManager.updateButtonStates(this.uiManager.getProductCount())
    this.uiManager.updateCounter(this.uiManager.getProductCount())
    
    const allIDs = this.state.persist()
    this.uiManager.updateHiddenField(allIDs)
  }

  clear(event) {
    event.preventDefault()
    
    this.state.clear()
    this.uiManager.uncheckAllCheckboxes()
    this.uiManager.removeAllCompareProducts()
    this.uiManager.updateHiddenField([])
    this.updateUI()
  }
}
